// src/context/PWAContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const PWAContext = createContext();
export const usePWA = () => useContext(PWAContext);

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // 1. Check if user already dismissed the popup previously
    const dismissed = localStorage.getItem('bma_pwa_dismissed') === 'true';
    setIsDismissed(dismissed);

    // 2. Check if iOS
    const checkIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(checkIOS);

    // 3. Check if already installed (Standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    // 4. Listen for native Android/Desktop install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Prevent automatic Chrome prompt
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // Function to actually trigger the installation
  const promptInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsStandalone(true); // Hide buttons after successful install
      }
    } else if (isIOS) {
      alert('To install on iOS: Tap the Share button at the bottom of Safari, then tap "Add to Home Screen".');
    } else {
      // Fallback for testing on localhost without proper PWA setup
      alert("Note: You are running on localhost without a full PWA setup. The UI works, but actual installation requires HTTPS and a valid manifest.");
    }
  };

  // Function to dismiss the popup and show header button instead
  const dismissPrompt = () => {
    setIsDismissed(true);
    localStorage.setItem('bma_pwa_dismissed', 'true');
  };

  return (
    <PWAContext.Provider value={{ 
      deferredPrompt, 
      isIOS, 
      isStandalone, 
      isDismissed, 
      promptInstall, 
      dismissPrompt 
    }}>
      {children}
    </PWAContext.Provider>
  );
};